import { AirTrackItem } from '../types/air_types';
import { TrackItem, Credentials, UrlParams } from '../types/types';
import { Dispatch, SetStateAction } from 'react';

const FALLBACK_API_BASE_URL = 'https://cargo.nova-tms.com/api/v1';

const normalizeApiBaseUrl = (url: string) => url.replace(/\/+$/, '');

const decodeApiUrl = (encoded: string | null): string | null => {
  if (!encoded) {
    return null;
  }

  try {
    const normalized = encoded.trim().replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
    const decoded = atob(padded).trim();
    return decoded || null;
  } catch {
    return null;
  }
};

const resolveApiBaseUrls = (apiUrlFromParams: string | null): string[] => {
  if (!apiUrlFromParams) {
    return [FALLBACK_API_BASE_URL];
  }

  const primary = normalizeApiBaseUrl(apiUrlFromParams);

  if (primary === FALLBACK_API_BASE_URL) {
    return [FALLBACK_API_BASE_URL];
  }

  return [primary, FALLBACK_API_BASE_URL];
};

const getParamsFromUrl = async (): Promise<UrlParams> =>{
   
    let query = new URLSearchParams(window.location.search);

    let session_id = query.get('session_id');
    let number = query.get('number');
    let instance_name = query.get('name');
    let reference_id = query.get('reference_id');
    let monitoring_status = query.get('monitoring_status');
    let api_url = decodeApiUrl(query.get('api_url'));

    let language = query.get('lang');
    let tmsLanguage = language ? language : "en";  

    let type = query.get('type');

    let urlParams: UrlParams ={
      session_id: session_id,
      number: number,
      instance_name: instance_name,
      language: tmsLanguage,
      reference_id: reference_id,
      monitoring_status: monitoring_status,
      monitoring_type: type,
      api_base_url: api_url,
    }
  
      return urlParams
}

const fetchWithAuth = async (url: string, method: string, token: string, body?: any) => {
    const options: RequestInit = {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    };

    if (body) {
      options.body = JSON.stringify(body);
    }
  
    const response = await fetch(url, options);
  
    if (!response.ok) {
      throw new Error(`Network response was not ok: ${response.statusText}`);
    }
  
    return response.json();
}


const handleRefreshToken = async (apiBaseUrl: string, refresh:string , token: string  ) => {
    const url = `${apiBaseUrl}/token/refresh/`; 
      
    const payload = { refresh: refresh };

    try {
      const data = await fetchWithAuth(url, 'POST', token, payload);
      return data.access;
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }

  };


const handleGetToken = async (apiBaseUrl: string, user:string, password: string ) => {

    const url = `${apiBaseUrl}/token/`;
      
    const payload = { 
        username: user,
        password: password
    };

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) {
        throw new Error('Failed to obtain tokens');
      }
  
      return response.json();
    } catch (error) {
      console.error('Error getting token:', error);
      throw error;
    }
  };


  const handleGetTrackingItem = async (apiBaseUrl: string, session_id:string, number: string ,refreshedData:string , reference_id:string | null , monitoring_type: string | null) => {
     

   try { 
    let url: string ;

    if(monitoring_type === 'SEA'){
      
       url =`${apiBaseUrl}/container/tracking/?session_id=${session_id}&number=${number}` + (reference_id ? `&reference_id=${reference_id}` : ''); //temporary if tracking session == null
    }else if(monitoring_type === 'AIR'){

       url =`${apiBaseUrl}/air/tracking/?session_id=${session_id}&number=${number}` + (reference_id ? `&reference_id=${reference_id}` : ''); 
    }else{
       throw new Error("Invalid transport type provided");
    }
      const data = await fetchWithAuth(url, 'GET', refreshedData);

      if (data.results && data.results.length > 0) {
               
        if (monitoring_type === 'SEA') {
          const result = (data.results as TrackItem[]).find(item => item.number === number);
          if (result) {
            return result;
          } else {
            throw new Error('No matching SEA tracking item found.');
          }

        } else if (monitoring_type === 'AIR') {
          const result = (data.results as AirTrackItem[]).find(item => item.number === number);

          if (result) {
            return result;
          } else {
            throw new Error('No matching AIR tracking item found.');
          }
        } else {
          throw new Error('Invalid monitoring type.');
        }

      } else {
        throw new Error('auth_no_tracking_id_found_in_the_response');
      }
  } catch (error) {
      console.error('Error getting tracking ID:', error);
      throw error;
  }
      
  };


const runAuthFlow = async (
  apiBaseUrl: string,
  credentials: Credentials,
  monitoring_type: string | null,
) => {
  const { user, password, session_id, number, reference_id } = credentials;

  const getToken = await handleGetToken(apiBaseUrl, user, password);
  if (!getToken) {
    throw new Error('auth_failed_to_get_the_token');
  }

  const refreshedData = await handleRefreshToken(apiBaseUrl, getToken.refresh, getToken.access);
  if (!refreshedData) {
    throw new Error('auth_the_token_has_not_been_updated');
  }

  return handleGetTrackingItem(
    apiBaseUrl,
    session_id,
    number,
    refreshedData,
    reference_id,
    monitoring_type,
  );
};

   
  const handleTokenRefreshAndTracking = async (
    apiUrlFromParams: string | null,
    credentials: Credentials,
    setTrackItem: Dispatch<SetStateAction<TrackItem | AirTrackItem| null>>, 
    setLoading: Dispatch<SetStateAction<boolean>>, 
    setError: Dispatch<SetStateAction<string>>,
    setLanguage: Dispatch<SetStateAction<string | null>>,
    monitoring_type: string | null,
    ) => {
     const { language } = credentials;
     const apiBaseUrls = resolveApiBaseUrls(apiUrlFromParams);

    try {
        setLoading(true);
        setError('');

        let lastError: unknown;

        for (const apiBaseUrl of apiBaseUrls) {
          try {
            const trackingItem = await runAuthFlow(apiBaseUrl, credentials, monitoring_type);
            setTrackItem(trackingItem);
            setLanguage(language);
            return;
          } catch (error) {
            lastError = error;
            console.warn(`API failed for ${apiBaseUrl}, trying next...`, error);
          }
        }

        throw lastError;
    } catch (error) {
        setError('auth_error_occured_please_try_again');
    } finally {
        setLoading(false);
    }
};
 

export{
    handleRefreshToken,
    handleTokenRefreshAndTracking,
    handleGetToken,
    getParamsFromUrl
}
